import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'util/helpers';
import * as cssVariable from 'cssVariables';
import { injectStyles } from 'injectStyles';
import classNames from 'classnames';
import SearchIcon from 'components/svg/Search';
import CloseIcon from 'components/svg/Close';
import { getPlaceholderPseudo, isMobile, isTouch } from 'util/device';

const inputPlaceholder = {
  ZOEKEN: 'Zoeken..',
  ZOEKEN_MOBILE: 'Zoeken binnen JUKE',
};

const searchIconMobileTop = 18;
const closeButtonMobileTop = 30;

class Input extends PureComponent {
  static defaultProps = {
    hasMobileHeader: false,
    gigyaIsActive: false,
    page: null,
    showScrollNavigation: false,
  };

  static propTypes = {
    hasMobileHeader: PropTypes.bool,
    isActive: PropTypes.bool.isRequired,
    query: PropTypes.string.isRequired,
    onSetActive: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    hideMiniPlayer: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    gigyaIsActive: PropTypes.bool,
    page: PropTypes.object,
    showScrollNavigation: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      query: props.query,
    };
    this.debouncedSearch = debounce(this.props.onSearch, 500);
  }

  componentDidMount() {
    const { isActive, query } = this.props;
    if (isActive) {
      this.inputRef.focus();
      const strLength = query.length;
      this.inputRef.setSelectionRange(strLength, strLength);
    }
  }

  componentDidUpdate(prevProps) {
    const { isActive, query, onSearch } = this.props;
    if (prevProps.isActive !== isActive) {
      if (!isActive) {
        this.blurInput();
      } else if (query) {
        onSearch(query.trim());
      }
    }
  }

  bindInput = (input) => {
    this.inputRef = input;
  };

  blurInput = () => {
    this.setState({ focus: false });
    if (this.inputRef) {
      this.inputRef.blur();
    }
  };

  handleInputChange = (e) => {
    const query = e.target.value;
    this.setState({ query });
    this.debouncedSearch(query.trim());
  };

  clickOnInput = () => {
    const { onSetActive, hideMiniPlayer } = this.props;

    onSetActive(true);
    this.setState({ focus: true });
    setTimeout(() => {
      // select the text in the input field so the users can delete it as soon as they start typing
      // use setSelectionRange() instead of select() because of mobile browsers
      if (this.inputRef) {
        this.inputRef.setSelectionRange(0, this.inputRef.value.length);
      }
    }, 100);
    if (isTouch) {
      hideMiniPlayer(true);
    }
  };

  handleInputBlur = () => {
    const { isActive, hideMiniPlayer } = this.props;

    if (!isActive) {
      this.setState({ focus: false });
    }
    if (isTouch) {
      hideMiniPlayer(false);
    }
  };

  handleKeyDown = (e) => {
    this.props.onSetActive(true);
    if (e.key === 'Escape' || e.nativeEvent.keyCode === 27) {
      this.inputRef.blur();
      this.setState({ focus: false });
      this.props.onSetActive(false);
    }
  };

  handleOnSubmit = (e) => {
    e.preventDefault();
    // hide keyboard on touch devices
    if (isTouch) {
      this.inputRef.blur();
    }
  };

  renderPlaceholder = () => {
    const { hasMobileHeader, isActive } = this.props;

    if (hasMobileHeader && isActive) {
      return inputPlaceholder.ZOEKEN_MOBILE;
    }

    if (hasMobileHeader && !isActive) {
      return null;
    }

    return inputPlaceholder.ZOEKEN;
  };

  render() {
    const { hasMobileHeader, isActive, classes, onSetActive, gigyaIsActive, showScrollNavigation } = this.props;

    const { query, focus } = this.state;

    const containerClass = classNames({
      [classes.container]: !isMobile,
      [classes.containerActive]: isActive,
      [classes.hideMobileInput]: hasMobileHeader && !showScrollNavigation,
      [classes.inputFocused]: focus,
      [classes.mobileContainer]: hasMobileHeader,
    });

    // form is necessary to enable the mobile feature of "Search" button on the native keyboard on Safari mobile
    // the input field must have name search and "search" containing id
    // the active state of the input should be the same for all pages since it it in the results screen
    // that is why the isActive and bigSearch set sometimes the same classes
    return (
      <div className={classNames(containerClass, {})}>
        {!(isTouch && gigyaIsActive) && (
          <form onSubmit={this.handleOnSubmit}>
            <input
              className={classNames(classes.inputField, {
                [classes.inputFieldActive]: isActive,
              })}
              data-testid="search_input"
              autoComplete="off"
              ref={this.bindInput}
              type={isTouch ? 'search' : 'text'}
              name="search"
              placeholder={this.renderPlaceholder()}
              value={isActive ? query : ''}
              onClick={this.clickOnInput}
              onFocus={this.clickOnInput}
              onBlur={this.handleInputBlur}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyDown}
              id="search-input"
            />
          </form>
        )}
        {(isActive || focus) && (
          <button
            type="button"
            className={classes.closeButton}
            data-testid="search_close"
            onClick={() => onSetActive(false)}
            tabIndex={isActive ? 0 : -1}
          >
            <CloseIcon />
          </button>
        )}

        {!isActive && !focus && (
          <div
            className={classNames(classes.searchIcon, {
              [classes.searchIconActive]: isActive,
            })}
          >
            <SearchIcon />
          </div>
        )}
        {isActive && hasMobileHeader && (
          <span className={classes.searchIconMobile}>
            <SearchIcon />
          </span>
        )}
      </div>
    );
  }
}

const placeholderPseudo = getPlaceholderPseudo();

const styles = {
  container: {
    position: 'relative',
    color: cssVariable.grey600,
    pointerEvents: 'all',
    zIndex: 3,
    overflow: 'visible',
    width: 200,
    marginLeft: 0,
    marginTop: 30,
    borderRadius: 4,
    outline: 'none',
    transition: 'width 0.4s ease',

    '&:focus': {
      outline: 'none',
      appearance: 'none',
      '&::-ms-clear': {
        display: 'none',
      },
    },

    '&:hover $searchIcon': {
      color: cssVariable.black,
    },
  },
  containerActive: {
    pointerEvents: 'all',
    marginLeft: 0,
    background: cssVariable.white,
  },
  hideMobileInput: {
    display: 'none',
  },
  searchIcon: {
    pointerEvents: 'none',
    position: 'absolute',
    top: '50%',
    right: 0,
    transition: `transform ${cssVariable.transitionDelay} ease`,
    transitionDelay: cssVariable.transitionDelay,
    transform: 'translate(0, -50%)',
    color: cssVariable.white,
    paddingRight: 15,
    verticalAlign: 'middle',
    '& svg': {
      width: 20,
      height: 20,
    },
  },

  inputField: {
    position: 'relative',
    width: '100%',
    lineHeight: 'normal',
    fontWeight: cssVariable.fontWeights.light,
    transition: 'all 300ms ease',
    transitionProperty: 'padding, font-size',
    // remove the x default button on ie and edge
    appearance: 'none',
    fontSize: cssVariable.fontSizes.small,
    height: 50,
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 4,
    padding: '10px 40px 10px 19px',
    border: '1px solid transparent',
    textSizeAdjust: '100%',

    [placeholderPseudo]: {
      fontSize: cssVariable.fontSizes.small,
      color: cssVariable.white,
    },
    '&::-webkit-search-cancel-button': {
      display: 'none',
    },

    '&:hover': {
      border: `1px solid ${cssVariable.grey600}`,
      boxShadow: '0 1px 4px 0 rgba(0,0,0,0.1), 0 5px 10px 0 rgba(0,0,0,0.1)',
      background: cssVariable.white,
      [placeholderPseudo]: {
        color: cssVariable.black,
      },
    },

    '&:focus': {
      border: `1px solid ${cssVariable.green500} !important`,
      caretColor: cssVariable.green500,
      boxShadow: '0 5px 20px 0 rgba(147, 192, 31, 0.2) !important',
      textSizeAdjust: '100%',
      background: cssVariable.white,
      [placeholderPseudo]: {
        color: `${cssVariable.grey500} !important`,
      },
    },

    '&::-ms-clear': {
      display: 'none',
    },
  },

  searchIconActive: {
    transitionDelay: 0,
    transform: 'translate(100%, -50%)',
  },

  closeButton: {
    position: 'absolute',
    top: '50%',
    right: 0,
    transition: `transform ${cssVariable.transitionDelay} ease`,
    transitionDelay: cssVariable.transitionDelay,
    transform: 'translate(0, -50%)',
    background: 'none',
    border: 0,
    color: cssVariable.grey600,
    lineHeight: 'normal',
    fontWeight: cssVariable.fontWeights.light,
    fontSize: cssVariable.fontSizes.small,
    padding: cssVariable.gutter,

    '&:hover': {
      cursor: 'pointer',
    },

    '& svg': {
      width: 10,
      height: 10,
      verticalAlign: 'middle',
      position: 'absolute',
      top: 15,
      right: 15,
    },
  },

  inputFocused: {
    width: '100%',
    background: cssVariable.white,
  },

  searchIconMobile: {
    position: 'absolute',
    top: searchIconMobileTop,
    left: 16,
    '& svg': {
      width: 26,
      height: 26,
    },
  },

  inputFieldActive: {
    backgroundColor: cssVariable.grey100,
    [placeholderPseudo]: {
      color: cssVariable.grey500,
    },
  },
  mobileContainer: {
    position: 'fixed',
    top: 0,
    right: 55,
    width: 50,
    height: 60,
    margin: 0,
    left: 'auto',
    '&$containerActive': {
      zIndex: 2,
      marginTop: 0,
      position: 'fixed',
      left: 0,
      width: '100%',
    },

    '& $inputField': {
      position: 'fixed',
      top: 0,
      left: 'auto',
      right: 50,
      width: 50,
      height: cssVariable.mobileHeaderHeight,
      textSizeAdjust: '100%',
      fontSize: cssVariable.fontSizes.small,
    },

    '& $inputFieldActive': {
      left: 0,
      right: 0,
      width: '100%',
      paddingLeft: 56,
    },
    '& $closeButton': {
      top: closeButtonMobileTop,
      right: 0,
      width: 60,
      height: 40,
      padding: 0,
      '& svg': {
        width: 12,
        height: 12,
        right: 20,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 5,
        right: 50,
        height: 30,
        width: 1,
        backgroundColor: cssVariable.grey400,
      },
    },
  },
};

export default injectStyles(styles)(Input);
